import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '@/components/Layout';
import { API } from '@/App';
import { formatDate } from '@/utils/dateFormat';
import { 
  FileBarChart, Search, Calendar, IndianRupee, Store, 
  Clock, CheckCircle, XCircle, AlertCircle, Download, Trash2,
  Link, Eye, Check, FileSpreadsheet, Building2, BarChart3,
  MessageSquare, Send, RotateCcw, Edit3, Mail, ArrowLeft
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { VendorSubmissionsContent } from '@/pages/VendorSubmissions';

export default function VendorQuotationsReceived() {
  const navigate = useNavigate();
  const [quotations, setQuotations] = useState([]);
  const [quoteSubmissions, setQuoteSubmissions] = useState([]);
  const [additionalItems, setAdditionalItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedQuotation, setSelectedQuotation] = useState(null);
  const [deleteReason, setDeleteReason] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [viewSubmissionDialogOpen, setViewSubmissionDialogOpen] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [viewAdditionalDialogOpen, setViewAdditionalDialogOpen] = useState(false);
  const [selectedAdditional, setSelectedAdditional] = useState(null);
  const [activeTab, setActiveTab] = useState('submissions');
  
  // Action dialog state
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [actionType, setActionType] = useState(null);
  const [actionRemarks, setActionRemarks] = useState('');
  const [actionSubmissionId, setActionSubmissionId] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Convert to BOQ state
  const [convertToBOQLoading, setConvertToBOQLoading] = useState(false);

  // Amend dialog state
  const [amendDialogOpen, setAmendDialogOpen] = useState(false);
  const [amendReason, setAmendReason] = useState('');
  const [amendSubmissionId, setAmendSubmissionId] = useState(null);
  const [amendLoading, setAmendLoading] = useState(false);
  const [amendItems, setAmendItems] = useState([]);
  const [amendLumpSumDiscount, setAmendLumpSumDiscount] = useState(0);
  const [amendDiscountType, setAmendDiscountType] = useState('none'); // none, line_item, lump_sum

  // CC emails state for main amend dialog
  const [amendCcEmails, setAmendCcEmails] = useState('');

  // Additional items amend state
  const [amendAdditionalDialogOpen, setAmendAdditionalDialogOpen] = useState(false);
  const [amendAdditionalId, setAmendAdditionalId] = useState(null);
  const [amendAdditionalReason, setAmendAdditionalReason] = useState('');
  const [amendAdditionalItems, setAmendAdditionalItems] = useState([]);
  const [amendAdditionalLoading, setAmendAdditionalLoading] = useState(false);
  const [amendAdditionalCcEmails, setAmendAdditionalCcEmails] = useState('');
  const [amendAdditionalDiscountType, setAmendAdditionalDiscountType] = useState('none');
  const [amendAdditionalLumpSumDiscount, setAmendAdditionalLumpSumDiscount] = useState(0);
  const [chatDialogOpen, setChatDialogOpen] = useState(false);
  const [chatSubmission, setChatSubmission] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [chatSending, setChatSending] = useState(false);

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = ['owner', 'staff', 'admin'].includes(user.role);

  useEffect(() => {
    fetchQuotations();
    fetchQuoteSubmissions();
    fetchAdditionalItems();
    
    // Poll for new submissions every 15 seconds
    const pollInterval = setInterval(() => {
      fetchQuoteSubmissions();
      fetchAdditionalItems();
    }, 15000);
    return () => clearInterval(pollInterval);
  }, []);

  const fetchQuotations = async () => {
    try {
      const response = await axios.get(`${API}/quotations`);
      const vendorQuotations = response.data.filter(q => q.quotation_type === 'vendor' || q.vendor_id);
      setQuotations(vendorQuotations);
    } catch (error) {
      console.error('Failed to load quotations:', error);
    }
  };

  const fetchQuoteSubmissions = async () => {
    try {
      const response = await axios.get(`${API}/vendor-quote-submissions`);
      setQuoteSubmissions(response.data);
    } catch (error) {
      console.error('Failed to load quote submissions:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAdditionalItems = async () => {
    try {
      const response = await axios.get(`${API}/vendor-additional-items`);
      setAdditionalItems(response.data);
    } catch (error) {
      console.error('Failed to load additional items:', error);
    }
  };

  const handleDelete = async () => {
    if (!deleteReason.trim() || deleteReason.trim().length < 5) {
      toast.error('Please provide a reason (at least 5 characters)');
      return;
    }
    setDeleting(true);
    try {
      await axios.delete(`${API}/quotations/${selectedQuotation.id}`, { data: { reason: deleteReason.trim() } });
      toast.success('Quotation permanently deleted');
      setDeleteDialogOpen(false);
      setSelectedQuotation(null);
      setDeleteReason('');
      fetchQuotations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete quotation');
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteSubmission = async (submissionId) => {
    if (!window.confirm('Are you sure you want to delete this vendor quote submission? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/vendor-quote-submissions/${submissionId}`);
      toast.success('Quote submission deleted');
      fetchQuoteSubmissions();
      setViewSubmissionDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete submission');
    }
  };

  const openActionDialog = (submissionId, type) => {
    setActionSubmissionId(submissionId);
    setActionType(type);
    setActionRemarks('');
    setActionDialogOpen(true);
  };

  const handleSubmitAction = async () => {
    if (!actionRemarks.trim() && actionType === 'reject') {
      toast.error('Please provide a reason for rejection');
      return;
    }
    setActionLoading(true);
    try {
      const endpoint = actionType === 'accept' 
        ? `${API}/vendor-quote-submissions/${actionSubmissionId}/accept`
        : `${API}/vendor-quote-submissions/${actionSubmissionId}/reject`;
      await axios.post(endpoint, { remarks: actionRemarks });
      toast.success(actionType === 'accept' ? 'Quote accepted! Rates saved to Vendor BOQ.' : 'Quote rejected with remarks');
      setActionDialogOpen(false);
      fetchQuoteSubmissions();
      setViewSubmissionDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${actionType} quote`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleConvertToBOQ = async (submission) => {
    if (!window.confirm(`Convert this vendor quote from ${submission.vendor_name} into a new BOQ worksheet?`)) return;
    setConvertToBOQLoading(true);
    try {
      const response = await axios.post(`${API}/vendor-quote-submissions/${submission.id}/convert-to-boq`, { worksheet_name: `Vendor Quote - ${submission.vendor_name}` });
      toast.success(response.data.message);
      fetchQuoteSubmissions();
      setViewSubmissionDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to convert to BOQ');
    } finally {
      setConvertToBOQLoading(false);
    }
  };

  // Amend handler
  const openAmendDialog = (submissionId) => {
    const sub = quoteSubmissions.find(s => s.id === submissionId) || selectedSubmission;
    setAmendSubmissionId(submissionId);
    setAmendReason('');
    setAmendCcEmails('');
    setAmendLumpSumDiscount(0);
    setAmendDiscountType('none');
    setAmendItems((sub?.items || []).map(item => ({
      item_id: item.item_id || item.task_id || item.id,
      title: item.task_title || item.title || item.description || '',
      description: item.description || '',
      category_name: item.category_name || '',
      unit: item.unit || 'Nos',
      rate: item.vendor_rate || item.rate || 0,
      quantity: item.quantity || 1,
      discount_pct: 0,
      remarks: '',
      original_rate: item.vendor_rate || item.rate || 0,
      original_quantity: item.quantity || 1,
    })));
    setAmendDialogOpen(true);
  };

  const updateAmendItem = (idx, field, value) => {
    setAmendItems(prev => prev.map((item, i) => i === idx ? { ...item, [field]: value } : item));
  };

  const handleSubmitAmend = async () => {
    if (!amendReason.trim()) { toast.error('Please provide a reason for amendment'); return; }
    setAmendLoading(true);
    try {
      const changedItems = amendItems.filter(item => 
        item.rate !== item.original_rate || item.quantity !== item.original_quantity || item.remarks.trim() || item.discount_pct > 0
      ).map(item => ({
        item_id: item.item_id,
        title: item.title,
        rate: item.rate !== item.original_rate ? item.rate : null,
        quantity: item.quantity !== item.original_quantity ? item.quantity : null,
        discount_pct: item.discount_pct > 0 ? item.discount_pct : null,
        remarks: item.remarks || '',
      }));
      const payload = {
        reason: amendReason.trim(),
        amended_items: changedItems.length > 0 ? changedItems : undefined,
        cc_emails: amendCcEmails.trim() || undefined,
      };
      if (amendDiscountType === 'lump_sum' && amendLumpSumDiscount > 0) {
        payload.lump_sum_discount_percentage = amendLumpSumDiscount;
      }
      const response = await axios.post(`${API}/vendor-quote-submissions/${amendSubmissionId}/request-amend`, payload);
      if (response.data.email_sent) {
        toast.success('Amendment request sent. Email delivered to vendor.');
      } else {
        toast.success('Amendment saved. Email delivery failed - please follow up manually.');
      }
      setAmendDialogOpen(false);
      fetchQuoteSubmissions();
      setViewSubmissionDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to request amendment');
    } finally {
      setAmendLoading(false);
    }
  };

  // Chat handlers
  const openChatDialog = async (submission) => {
    setChatSubmission(submission);
    setChatDialogOpen(true);
    setChatLoading(true);
    try {
      const response = await axios.get(`${API}/vendor-quote-submissions/${submission.id}/chat`);
      setChatMessages(response.data);
    } catch (error) {
      console.error('Failed to load chat:', error);
      setChatMessages([]);
    } finally {
      setChatLoading(false);
    }
  };

  const handleSendChat = async () => {
    if (!chatInput.trim() || !chatSubmission) return;
    setChatSending(true);
    try {
      const response = await axios.post(`${API}/vendor-quote-submissions/${chatSubmission.id}/chat`, { message: chatInput.trim() });
      setChatMessages(prev => [...prev, response.data]);
      setChatInput('');
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setChatSending(false);
    }
  };

  // Additional Items Amend handlers
  const openAmendAdditionalDialog = (submission) => {
    setAmendAdditionalId(submission.id);
    setAmendAdditionalReason('');
    setAmendAdditionalCcEmails('');
    setAmendAdditionalDiscountType('none');
    setAmendAdditionalLumpSumDiscount(0);
    setAmendAdditionalItems((submission.items || []).map((item, idx) => ({
      idx,
      description: item.description || '',
      unit: item.unit || 'Sqft',
      rate: item.rate || 0,
      quantity: item.quantity || 1,
      discount_pct: 0,
      remarks: '',
      original_rate: item.rate || 0,
      original_quantity: item.quantity || 1,
    })));
    setAmendAdditionalDialogOpen(true);
  };

  const updateAmendAdditionalItem = (idx, field, value) => {
    setAmendAdditionalItems(prev => prev.map((item, i) => i === idx ? { ...item, [field]: value } : item));
  };

  const handleSubmitAmendAdditional = async () => {
    if (!amendAdditionalReason.trim()) { toast.error('Please provide a reason'); return; }
    setAmendAdditionalLoading(true);
    try {
      const changedItems = amendAdditionalItems.filter(item =>
        item.rate !== item.original_rate || item.quantity !== item.original_quantity || item.remarks.trim() || item.discount_pct > 0
      ).map(item => ({
        idx: item.idx,
        description: item.description,
        rate: item.rate !== item.original_rate ? item.rate : null,
        quantity: item.quantity !== item.original_quantity ? item.quantity : null,
        discount_pct: item.discount_pct > 0 ? item.discount_pct : null,
        remarks: item.remarks || '',
      }));
      const payload = {
        reason: amendAdditionalReason.trim(),
        amended_items: changedItems.length > 0 ? changedItems : undefined,
        cc_emails: amendAdditionalCcEmails.trim() || undefined,
      };
      if (amendAdditionalDiscountType === 'lump_sum' && amendAdditionalLumpSumDiscount > 0) {
        payload.lump_sum_discount_percentage = amendAdditionalLumpSumDiscount;
      }
      const resp = await axios.post(`${API}/vendor-additional-items/${amendAdditionalId}/request-amend`, payload);
      if (resp.data.email_sent) {
        toast.success('Amendment request sent. Email delivered to vendor.');
      } else {
        toast.success('Amendment saved. Email delivery failed - please follow up manually.');
      }
      setAmendAdditionalDialogOpen(false);
      fetchAdditionalItems();
      setViewAdditionalDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to request amendment');
    } finally {
      setAmendAdditionalLoading(false);
    }
  };


  // Additional Items handlers
  const handleAcceptAdditional = async (itemId) => {
    try {
      await axios.post(`${API}/vendor-additional-items/${itemId}/accept`, { remarks: '' });
      toast.success('Vendor additional items accepted');
      fetchAdditionalItems();
      setViewAdditionalDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to accept');
    }
  };

  const handleRejectAdditional = async (itemId) => {
    const reason = window.prompt('Reason for rejection (optional):');
    if (reason === null) return;
    try {
      await axios.post(`${API}/vendor-additional-items/${itemId}/reject`, { remarks: reason || '' });
      toast.success('Vendor additional items rejected');
      fetchAdditionalItems();
      setViewAdditionalDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject');
    }
  };

  const handleDeleteAdditional = async (itemId) => {
    if (!window.confirm('Delete this vendor additional items submission? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/vendor-additional-items/${itemId}`);
      toast.success('Vendor additional items deleted');
      fetchAdditionalItems();
      setViewAdditionalDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleConvertAdditionalToBOQ = async (submission) => {
    if (!window.confirm(`Convert additional items from ${submission.vendor_name} into BOQ tasks?`)) return;
    try {
      const response = await axios.post(`${API}/vendor-additional-items/${submission.id}/convert-to-boq`);
      toast.success(response.data.message);
      fetchAdditionalItems();
      setViewAdditionalDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to convert to BOQ');
    }
  };

  const getStatusBadge = (status) => {
    const map = {
      'pending_review': { cls: 'bg-amber-100 text-amber-700', label: 'PENDING REVIEW' },
      'pending': { cls: 'bg-amber-100 text-amber-700', label: 'PENDING' },
      'accepted': { cls: 'bg-green-100 text-green-700', label: 'ACCEPTED' },
      'approved': { cls: 'bg-green-100 text-green-700', label: 'APPROVED' },
      'rejected': { cls: 'bg-red-100 text-red-700', label: 'REJECTED' },
      'amendment_requested': { cls: 'bg-orange-100 text-orange-700', label: 'AMEND REQUESTED' },
    };
    const m = map[status] || { cls: 'bg-stone-100 text-stone-700', label: status?.toUpperCase() || 'UNKNOWN' };
    return <Badge className={m.cls}>{m.label}</Badge>;
  };

  const filteredQuotations = quotations.filter(q => 
    q.quotation_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    q.vendor_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    q.project_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="text-stone-400">Loading quotations...</div>
        </div>
      </Layout>
    );
  }

  // Vendor color assignment
  const vendorColors = [
    'border-l-purple-500 bg-purple-50/30',
    'border-l-blue-500 bg-blue-50/30',
    'border-l-green-500 bg-green-50/30',
    'border-l-amber-500 bg-amber-50/30',
    'border-l-red-500 bg-red-50/30',
    'border-l-teal-500 bg-teal-50/30',
    'border-l-pink-500 bg-pink-50/30',
  ];

  return (
    <Layout>
      <div className="space-y-6" data-testid="vendor-quotations-received">
        <div className="flex items-center justify-between">
          <div>
            {/* iter-338 (Msg-680): Back button — navigates to previous page
               instead of dropping the user on the dashboard. */}
            <div className="flex items-center gap-2 mb-2">
              <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="vendor-quotations-back-btn">
                <ArrowLeft size={14} className="mr-1" />Back
              </Button>
            </div>
            <h1 className="text-2xl font-bold text-stone-800 flex items-center gap-2">
              <Store className="text-purple-600" size={24} />
              Quotes &amp; Submissions
            </h1>
            <p className="text-stone-500 text-sm">All vendor quotes, additional items, traditional quotes, and vendor-submitted estimates &amp; running bills in one place</p>
          </div>
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" size={18} />
          <Input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search by vendor or quotation..." className="pl-10" />
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-4">
          <div className="apple-card p-4">
            <p className="text-2xl font-bold text-purple-600">{quoteSubmissions.length + quotations.length + additionalItems.length}</p>
            <p className="text-sm text-stone-500">Total Received</p>
          </div>
          <div className="apple-card p-4">
            <p className="text-2xl font-bold text-blue-600">{quoteSubmissions.length}</p>
            <p className="text-sm text-stone-500">Quote Link Submissions</p>
          </div>
          <div className="apple-card p-4">
            <p className="text-2xl font-bold text-amber-600">
              {quoteSubmissions.filter(q => q.status === 'pending' || q.status === 'pending_review').length + additionalItems.filter(a => a.status === 'pending_review').length}
            </p>
            <p className="text-sm text-stone-500">Pending Review</p>
          </div>
          <div className="apple-card p-4">
            <p className="text-2xl font-bold text-stone-600">
              ₹{((quoteSubmissions.reduce((sum, q) => sum + (q.total_amount || 0), 0) + quotations.reduce((sum, q) => sum + (q.total || 0), 0) + additionalItems.reduce((sum, a) => sum + (a.total_amount || 0), 0)) / 100000).toFixed(1)}L
            </p>
            <p className="text-sm text-stone-500">Total Value</p>
          </div>
        </div>

        {/* Tabs - 3 columns, visually distinct */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="grid w-full max-w-4xl grid-cols-4 h-auto p-1" data-testid="vendor-tabs">
            <TabsTrigger value="submissions" className="flex items-center gap-1.5 py-2.5 text-xs sm:text-sm data-[state=active]:bg-blue-600 data-[state=active]:text-white" data-testid="tab-submissions">
              <Link size={15} />
              <span>Quote Submissions ({quoteSubmissions.length})</span>
            </TabsTrigger>
            <TabsTrigger value="additional" className="flex items-center gap-1.5 py-2.5 text-xs sm:text-sm data-[state=active]:bg-amber-600 data-[state=active]:text-white" data-testid="tab-additional">
              <AlertCircle size={15} />
              <span>Additional Items ({additionalItems.length})</span>
            </TabsTrigger>
            <TabsTrigger value="quotations" className="flex items-center gap-1.5 py-2.5 text-xs sm:text-sm data-[state=active]:bg-purple-600 data-[state=active]:text-white" data-testid="tab-traditional">
              <FileBarChart size={15} />
              <span>Traditional Quotes ({quotations.length})</span>
            </TabsTrigger>
            {/* iter-310: merged "Vendor Submissions" — vendor-portal quotes/estimates/bills */}
            <TabsTrigger value="vendor-submissions" className="flex items-center gap-1.5 py-2.5 text-xs sm:text-sm data-[state=active]:bg-emerald-600 data-[state=active]:text-white" data-testid="tab-vendor-submissions">
              <FileBarChart size={15} />
              <span>Vendor Submissions</span>
            </TabsTrigger>
          </TabsList>

          {/* ========== QUOTE LINK SUBMISSIONS TAB ========== */}
          <TabsContent value="submissions">
            {quoteSubmissions.length === 0 ? (
              <div className="apple-card p-8 text-center">
                <Link className="mx-auto mb-4 text-stone-300" size={48} />
                <p className="text-stone-500">No vendor quote submissions received yet</p>
                <p className="text-sm text-stone-400 mt-2">Create a quote link from the Tasks page to invite vendors</p>
              </div>
            ) : (
              <div className="space-y-3">
                {(() => {
                  const vendorColorMap = {};
                  let colorIdx = 0;
                  const filtered = quoteSubmissions.filter(s => 
                    s.vendor_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    s.project_name?.toLowerCase().includes(searchQuery.toLowerCase())
                  );
                  const grouped = filtered.reduce((acc, sub) => {
                    const proj = sub.project_name || 'Unknown Project';
                    if (!acc[proj]) acc[proj] = [];
                    acc[proj].push(sub);
                    if (!vendorColorMap[sub.vendor_name]) {
                      vendorColorMap[sub.vendor_name] = vendorColors[colorIdx % vendorColors.length];
                      colorIdx++;
                    }
                    return acc;
                  }, {});
                  
                  return Object.entries(grouped).map(([projectName, subs]) => (
                    <div key={projectName} className="mb-6" data-testid={`vendor-project-group-${projectName}`}>
                      <div className="flex items-center gap-2 mb-3 pb-2 border-b border-stone-200">
                        <Building2 size={18} className="text-stone-600" />
                        <h3 className="font-semibold text-stone-800 text-lg">{projectName}</h3>
                        <Badge variant="secondary" className="text-xs">{subs.length} vendor{subs.length !== 1 ? 's' : ''}</Badge>
                      </div>
                      <div className="space-y-3 pl-2">
                        {subs.map((submission) => (
                          <div key={submission.id} className={`apple-card p-4 hover:shadow-md transition-shadow border-l-4 ${vendorColorMap[submission.vendor_name] || 'border-l-stone-300'}`}>
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1 flex-wrap">
                                  <h3 className="font-semibold text-stone-900">{submission.vendor_name}</h3>
                                  {getStatusBadge(submission.status)}
                                  <Badge className="bg-blue-100 text-blue-700">
                                    <Link size={12} className="mr-1" /> Quote Link
                                  </Badge>
                                </div>
                                <p className="text-sm text-stone-600">{submission.vendor_email}</p>
                                <p className="text-sm text-stone-500">{submission.items?.length || 0} items</p>
                                <div className="flex items-center gap-4 mt-2 text-xs text-stone-400">
                                  <span className="flex items-center gap-1">
                                    <Calendar size={12} /> Submitted {formatDate(submission.submitted_at)}
                                  </span>
                                </div>
                              </div>
                              <div className="flex items-center gap-3">
                                <div className="text-right">
                                  <p className="text-sm text-stone-400">Total Amount</p>
                                  <p className="text-xl font-bold text-purple-600">₹{(submission.total_amount || 0).toLocaleString()}</p>
                                  {/* iter-384 — surface admin's negotiated amended total right below the vendor's submitted total so
                                      the negotiation trail is visible at a glance without opening the amend dialog. */}
                                  {submission.lump_sum_discount_percentage > 0 && (() => {
                                    const disc = Number(submission.lump_sum_discount_percentage) || 0;
                                    const amended = Math.round((submission.total_amount || 0) * (1 - disc / 100));
                                    return (
                                      <div className="mt-1 text-right" data-testid={`amended-total-${submission.id}`}>
                                        <p className="text-[10px] text-red-600 leading-tight">Less {disc}% lump-sum discount</p>
                                        <p className="text-sm font-bold text-red-700 leading-tight">
                                          Amended: ₹{amended.toLocaleString('en-IN')}
                                        </p>
                                      </div>
                                    );
                                  })()}
                                </div>
                                <div className="flex flex-col gap-1.5">
                                  <Button variant="outline" size="sm" onClick={() => { setSelectedSubmission(submission); setViewSubmissionDialogOpen(true); }} className="text-blue-600 hover:bg-blue-50" data-testid={`view-submission-${submission.id}`}>
                                    <Eye size={14} className="mr-1" /> View
                                  </Button>
                                  {/* Chat button */}
                                  <Button variant="outline" size="sm" onClick={() => openChatDialog(submission)} className="text-indigo-600 hover:bg-indigo-50" data-testid={`chat-submission-${submission.id}`}>
                                    <MessageSquare size={14} className="mr-1" /> Chat
                                  </Button>
                                  {(submission.status === 'pending' || submission.status === 'pending_review') && (
                                    <>
                                      <Button size="sm" onClick={() => openActionDialog(submission.id, 'accept')} className="bg-green-600 hover:bg-green-700 text-white" data-testid={`accept-${submission.id}`}>
                                        <Check size={14} className="mr-1" /> Accept
                                      </Button>
                                      <Button variant="outline" size="sm" onClick={() => openAmendDialog(submission.id)} className="text-orange-600 hover:bg-orange-50" data-testid={`amend-${submission.id}`}>
                                        <Edit3 size={14} className="mr-1" /> Amend
                                      </Button>
                                      <Button variant="outline" size="sm" onClick={() => openActionDialog(submission.id, 'reject')} className="text-red-600 hover:bg-red-50" data-testid={`reject-${submission.id}`}>
                                        <XCircle size={14} className="mr-1" /> Reject
                                      </Button>
                                    </>
                                  )}
                                  {submission.status === 'amendment_requested' && (
                                    <>
                                      <Badge className="bg-orange-100 text-orange-700 text-xs"><RotateCcw size={12} className="mr-1" /> Amend Sent</Badge>
                                      <Button size="sm" onClick={() => openActionDialog(submission.id, 'accept')} className="bg-green-600 hover:bg-green-700 text-white" data-testid={`accept-amended-${submission.id}`}>
                                        <Check size={14} className="mr-1" /> Accept as Amended
                                      </Button>
                                      <Button variant="outline" size="sm" onClick={() => openAmendDialog(submission.id)} className="text-orange-600 hover:bg-orange-50">
                                        <Edit3 size={14} className="mr-1" /> Re-Amend
                                      </Button>
                                    </>
                                  )}
                                  {(submission.status === 'accepted' || submission.status === 'approved') && !submission.converted_to_boq && (
                                    <Button size="sm" variant="outline" onClick={() => handleConvertToBOQ(submission)} disabled={convertToBOQLoading} className="text-purple-600 hover:bg-purple-50" data-testid={`convert-to-boq-${submission.id}`}>
                                      <FileSpreadsheet size={14} className="mr-1" /> Convert to BOQ
                                    </Button>
                                  )}
                                  {submission.converted_to_boq && (
                                    <Badge className="bg-purple-100 text-purple-700"><FileSpreadsheet size={12} className="mr-1" /> Converted</Badge>
                                  )}
                                  {(user.role === 'owner' || user.role === 'admin') && (
                                    <Button variant="outline" size="sm" onClick={() => handleDeleteSubmission(submission.id)} className="text-red-400 hover:text-red-600 hover:bg-red-50" data-testid={`delete-${submission.id}`}>
                                      <Trash2 size={14} className="mr-1" /> Delete
                                    </Button>
                                  )}
                                </div>
                              </div>
                            </div>
                            {/* iter-385 — Amendment History Trail: timeline strip showing every amend request sent to the vendor
                                so admins have a full audit log of the negotiation directly on the submission card. */}
                            {Array.isArray(submission.amendment_history) && submission.amendment_history.length > 0 && (
                              <div className="mt-3 pt-3 border-t border-stone-200" data-testid={`amendment-trail-${submission.id}`}>
                                <div className="flex items-center gap-2 mb-2">
                                  <RotateCcw size={12} className="text-orange-600" />
                                  <p className="text-xs font-semibold text-stone-700 uppercase tracking-wide">
                                    Amendment History ({submission.amendment_history.length})
                                  </p>
                                </div>
                                <ol className="relative border-l-2 border-orange-200 ml-2 space-y-2.5">
                                  {submission.amendment_history.map((amend, idx) => {
                                    const disc = Number(amend.lump_sum_discount_percentage) || 0;
                                    const itemCount = Array.isArray(amend.amended_items) ? amend.amended_items.length : 0;
                                    return (
                                      <li key={idx} className="ml-3 relative" data-testid={`amendment-event-${submission.id}-${idx}`}>
                                        <span className="absolute -left-[17px] top-1 flex items-center justify-center w-3 h-3 rounded-full bg-orange-500 ring-2 ring-white" />
                                        <div className="bg-orange-50 border border-orange-200 rounded-md px-3 py-2">
                                          <div className="flex items-center justify-between flex-wrap gap-1">
                                            <span className="text-xs font-semibold text-orange-900">
                                              #{idx + 1} · {amend.requested_by_name || 'Admin'}
                                            </span>
                                            <span className="text-[11px] text-stone-500">
                                              {amend.requested_at ? formatDate(amend.requested_at, { includeTime: true }) : ''}
                                            </span>
                                          </div>
                                          {amend.reason && (
                                            <p className="text-xs text-stone-700 mt-1 whitespace-pre-wrap">{amend.reason}</p>
                                          )}
                                          <div className="flex items-center gap-3 mt-1 text-[11px] text-stone-600 flex-wrap">
                                            {itemCount > 0 && (
                                              <span className="inline-flex items-center gap-1">
                                                <Edit3 size={10} /> {itemCount} item{itemCount !== 1 ? 's' : ''} amended
                                              </span>
                                            )}
                                            {disc > 0 && (
                                              <span className="inline-flex items-center gap-1 text-red-600 font-semibold">
                                                Lump-sum discount: {disc}%
                                              </span>
                                            )}
                                            {amend.email_sent && (
                                              <span className="inline-flex items-center gap-1 text-emerald-600">
                                                <Mail size={10} /> Emailed
                                              </span>
                                            )}
                                          </div>
                                        </div>
                                      </li>
                                    );
                                  })}
                                </ol>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ));
                })()}
              </div>
            )}
          </TabsContent>

          {/* ========== ADDITIONAL ITEMS TAB ========== */}
          <TabsContent value="additional">
            {additionalItems.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-lg border border-stone-200">
                <AlertCircle size={40} className="mx-auto text-stone-300 mb-3" />
                <p className="text-stone-500">No additional items suggested by vendors yet</p>
                <p className="text-sm text-stone-400 mt-1">Vendors can suggest extra line items via the published vendor link</p>
              </div>
            ) : (
              <div className="space-y-3">
                {additionalItems
                  .filter(s => s.vendor_name?.toLowerCase().includes(searchQuery.toLowerCase()) || s.project_name?.toLowerCase().includes(searchQuery.toLowerCase()))
                  .map((submission) => (
                    <div key={submission.id} className="apple-card p-4 hover:shadow-md transition-shadow border-l-4 border-l-amber-500 bg-amber-50/20">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <h3 className="font-semibold text-stone-900">{submission.vendor_name}</h3>
                            {getStatusBadge(submission.status)}
                            <Badge className="bg-amber-100 text-amber-700"><AlertCircle size={12} className="mr-1" /> Vendor Link</Badge>
                          </div>
                          <p className="text-sm text-stone-600">{submission.vendor_email}</p>
                          <p className="text-sm text-stone-500">
                            {submission.project_name || 'N/A'} {submission.boq_number ? `(BOQ #${submission.boq_number})` : ''} {submission.worksheet_name ? `- ${submission.worksheet_name}` : ''} - {submission.items?.length || 0} items
                          </p>
                          <div className="flex items-center gap-4 mt-2 text-xs text-stone-400">
                            <span className="flex items-center gap-1"><Calendar size={12} /> Submitted {formatDate(submission.submitted_at)}</span>
                          </div>
                          {submission.notes && <p className="mt-1 text-xs text-stone-500 italic">Note: {submission.notes}</p>}
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="text-right">
                            <p className="text-sm text-stone-400">Total Amount</p>
                            <p className="text-xl font-bold text-amber-600">₹{(submission.total_amount || 0).toLocaleString('en-IN')}</p>
                          </div>
                          <div className="flex flex-col gap-1.5">
                            <Button variant="outline" size="sm" onClick={() => { setSelectedAdditional(submission); setViewAdditionalDialogOpen(true); }} className="text-blue-600 hover:bg-blue-50">
                              <Eye size={14} className="mr-1" /> View
                            </Button>
                            {submission.status === 'pending_review' && (
                              <>
                                <Button size="sm" onClick={() => handleAcceptAdditional(submission.id)} className="bg-green-600 hover:bg-green-700 text-white">
                                  <Check size={14} className="mr-1" /> Accept
                                </Button>
                                <Button variant="outline" size="sm" onClick={() => openAmendAdditionalDialog(submission)} className="text-orange-600 hover:bg-orange-50" data-testid={`amend-additional-${submission.id}`}>
                                  <Edit3 size={14} className="mr-1" /> Amend
                                </Button>
                                <Button variant="outline" size="sm" onClick={() => handleRejectAdditional(submission.id)} className="text-red-600 hover:bg-red-50">
                                  <XCircle size={14} className="mr-1" /> Reject
                                </Button>
                              </>
                            )}
                            {submission.status === 'amendment_requested' && (
                              <>
                                <Badge className="bg-orange-100 text-orange-700 text-xs"><RotateCcw size={12} className="mr-1" /> Amend Sent</Badge>
                                <Button size="sm" onClick={() => handleAcceptAdditional(submission.id)} className="bg-green-600 hover:bg-green-700 text-white" data-testid={`accept-amended-additional-${submission.id}`}>
                                  <Check size={14} className="mr-1" /> Accept as Amended
                                </Button>
                                <Button variant="outline" size="sm" onClick={() => openAmendAdditionalDialog(submission)} className="text-orange-600 hover:bg-orange-50">
                                  <Edit3 size={14} className="mr-1" /> Re-Amend
                                </Button>
                              </>
                            )}
                            {submission.status === 'accepted' && !submission.converted_to_boq && (
                              <Button size="sm" variant="outline" onClick={() => handleConvertAdditionalToBOQ(submission)} className="text-purple-600 hover:bg-purple-50">
                                <FileSpreadsheet size={14} className="mr-1" /> Convert to BOQ
                              </Button>
                            )}
                            {submission.converted_to_boq && <Badge className="bg-purple-100 text-purple-700"><FileSpreadsheet size={12} className="mr-1" /> Converted</Badge>}
                            {(user.role === 'owner' || user.role === 'admin') && (
                              <Button variant="outline" size="sm" onClick={() => handleDeleteAdditional(submission.id)} className="text-red-400 hover:text-red-600 hover:bg-red-50">
                                <Trash2 size={14} className="mr-1" /> Delete
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </TabsContent>

          {/* ========== TRADITIONAL QUOTES TAB ========== */}
          <TabsContent value="quotations">
            {filteredQuotations.length === 0 ? (
              <div className="apple-card p-8 text-center">
                <FileBarChart className="mx-auto mb-4 text-stone-300" size={48} />
                <p className="text-stone-500">No quotations received from vendors yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredQuotations.map((quot) => (
                  <div key={quot.id} className="apple-card p-4 hover:shadow-md transition-shadow border-l-4 border-l-purple-500 bg-purple-50/20">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold">{quot.quotation_number}</h3>
                          {getStatusBadge(quot.status)}
                          <Badge className="bg-purple-100 text-purple-700"><Store size={12} className="mr-1" /> From Vendor</Badge>
                        </div>
                        <p className="text-sm text-stone-600 font-medium">{quot.vendor_name || 'Unknown Vendor'}</p>
                        <p className="text-sm text-stone-500">{quot.project_name || 'No project'} - {quot.items?.length || 0} items</p>
                        <div className="flex items-center gap-4 mt-2 text-xs text-stone-400">
                          <span className="flex items-center gap-1"><Calendar size={12} /> Submitted {formatDate(quot.created_at)}</span>
                          {quot.valid_until && <span className="flex items-center gap-1"><Clock size={12} /> Valid until {formatDate(quot.valid_until)}</span>}
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="text-sm text-stone-400">Total Amount</p>
                          <p className="text-xl font-bold text-purple-600">₹{(quot.total || 0).toLocaleString()}</p>
                        </div>
                        <button onClick={() => { setSelectedQuotation(quot); setDeleteDialogOpen(true); }} className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg" data-testid={`delete-vendor-quotation-${quot.id}`}>
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* ========== VENDOR SUBMISSIONS TAB (iter-310 merged) ========== */}
          <TabsContent value="vendor-submissions">
            <VendorSubmissionsContent embedded={true} />
          </TabsContent>
        </Tabs>

        {/* ========== VIEW ADDITIONAL ITEMS DIALOG ========== */}
        <Dialog open={viewAdditionalDialogOpen} onOpenChange={setViewAdditionalDialogOpen}>
          <DialogContent className="max-w-6xl w-[95vw] max-h-[90vh] overflow-y-auto" data-testid="view-additional-dialog">
            <DialogHeader>
              <DialogTitle className="text-xl text-amber-700 flex items-center gap-2">
                <AlertCircle className="text-amber-600" /> Vendor Additional Items
              </DialogTitle>
            </DialogHeader>
            {selectedAdditional && (
              <div className="space-y-5">
                {/* Vendor & Project Info */}
                <div className="bg-stone-50 p-4 rounded-lg grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div><p className="text-xs text-stone-500">Vendor</p><p className="font-semibold text-stone-900">{selectedAdditional.vendor_name}</p>{selectedAdditional.vendor_email && <p className="text-sm text-stone-600">{selectedAdditional.vendor_email}</p>}</div>
                  <div><p className="text-xs text-stone-500">Project</p><p className="font-semibold text-stone-900">{selectedAdditional.project_name || 'N/A'}</p>{selectedAdditional.boq_number && <p className="text-sm text-stone-500">BOQ #{selectedAdditional.boq_number}</p>}</div>
                  <div><p className="text-xs text-stone-500">Worksheet</p><p className="font-semibold text-stone-900">{selectedAdditional.worksheet_name || 'N/A'}</p></div>
                  <div><p className="text-xs text-stone-500">Submitted</p><p className="font-semibold text-stone-900">{formatDate(selectedAdditional.submitted_at)}</p></div>
                  <div><p className="text-xs text-stone-500">Status</p>{getStatusBadge(selectedAdditional.status)}</div>
                  <div><p className="text-xs text-stone-500">Total Items</p><p className="font-semibold text-stone-900">{selectedAdditional.items?.length || 0} items</p></div>
                </div>

                {selectedAdditional.notes && <div className="bg-amber-50 p-3 rounded-lg text-sm text-amber-800"><strong>Vendor Notes:</strong> {selectedAdditional.notes}</div>}

                {selectedAdditional.last_amendment_request && (
                  <div className="bg-orange-50 p-3 rounded-lg border border-orange-200">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium text-orange-800">Amendment Requested:</p>
                      {selectedAdditional.last_amendment_request.email_sent === true
                        ? <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-100 px-2 py-0.5 rounded-full" data-testid="additional-email-sent-badge"><Mail size={10} /> Email Sent</span>
                        : selectedAdditional.last_amendment_request.email_sent === false
                          ? <span className="inline-flex items-center gap-1 text-xs font-medium text-red-700 bg-red-100 px-2 py-0.5 rounded-full" data-testid="additional-email-failed-badge"><AlertCircle size={10} /> Email Failed</span>
                          : null
                      }
                    </div>
                    <p className="text-sm text-orange-700 mt-1">{selectedAdditional.last_amendment_request.reason}</p>
                    <p className="text-xs text-orange-500 mt-1">By {selectedAdditional.last_amendment_request.requested_by_name} on {formatDate(selectedAdditional.last_amendment_request.requested_at)}</p>
                  </div>
                )}

                {/* Items Table */}
                <div>
                  <h4 className="font-semibold text-stone-900 mb-2">Additional Items ({selectedAdditional.items?.length || 0})</h4>
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-stone-100">
                        <tr>
                          <th className="p-2.5 text-left w-8">#</th>
                          <th className="p-2.5 text-left">Category</th>
                          <th className="p-2.5 text-left">Description</th>
                          <th className="p-2.5 text-right w-20">Qty</th>
                          <th className="p-2.5 text-center w-20">Unit</th>
                          <th className="p-2.5 text-right w-28">Rate ({'\u20B9'})</th>
                          <th className="p-2.5 text-right w-32">Amount ({'\u20B9'})</th>
                          <th className="p-2.5 text-left">Remarks</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedAdditional.items?.map((item, idx) => {
                          const rate = item.rate || 0;
                          const qty = item.quantity || 1;
                          const amount = item.amount || (rate * qty);
                          return (
                            <tr key={idx} className="border-t hover:bg-stone-50" data-testid={`additional-item-${idx}`}>
                              <td className="p-2.5 text-stone-400 text-xs">{idx + 1}</td>
                              <td className="p-2.5 text-xs text-stone-600">
                                {item.main_category_name}
                                {item.sub_category_name && <span className="block text-stone-400">{item.sub_category_name}</span>}
                              </td>
                              <td className="p-2.5">
                                <p className="font-medium text-stone-800">{item.description}</p>
                              </td>
                              <td className="p-2.5 text-right text-stone-700">{qty}</td>
                              <td className="p-2.5 text-center text-stone-600 text-xs">{item.unit || 'Sqft'}</td>
                              <td className="p-2.5 text-right font-semibold text-stone-800">{'\u20B9'}{rate.toLocaleString('en-IN')}</td>
                              <td className="p-2.5 text-right font-bold text-green-700">{'\u20B9'}{amount.toLocaleString('en-IN')}</td>
                              <td className="p-2.5 text-stone-500 max-w-[180px] truncate">{item.admin_remarks || item.remarks || '-'}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Financial Summary */}
                <div className="bg-stone-50 rounded-lg p-4 max-w-md ml-auto">
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-stone-600">Subtotal:</span>
                      <span className="font-semibold">{'\u20B9'}{(selectedAdditional.subtotal || selectedAdditional.total_amount || 0).toLocaleString('en-IN')}</span>
                    </div>
                    {(selectedAdditional.gst_percentage > 0) && (
                      <div className="flex justify-between">
                        <span className="text-stone-600">GST ({selectedAdditional.gst_percentage}%):</span>
                        <span className="font-medium text-blue-700">+{'\u20B9'}{(selectedAdditional.gst_amount || 0).toLocaleString('en-IN')}</span>
                      </div>
                    )}
                    {(selectedAdditional.discount_percentage > 0) && (
                      <div className="flex justify-between">
                        <span className="text-stone-600">Discount ({selectedAdditional.discount_percentage}%):</span>
                        <span className="font-medium text-red-600">-{'\u20B9'}{(selectedAdditional.discount_amount || 0).toLocaleString('en-IN')}</span>
                      </div>
                    )}
                    {(selectedAdditional.lump_sum_discount_percentage > 0) && (
                      <div className="flex justify-between">
                        <span className="text-stone-600">Lump-Sum Discount ({selectedAdditional.lump_sum_discount_percentage}%):</span>
                        <span className="font-medium text-red-600">-{'\u20B9'}{Math.round((selectedAdditional.total_amount || 0) * selectedAdditional.lump_sum_discount_percentage / 100).toLocaleString('en-IN')}</span>
                      </div>
                    )}
                    <div className="flex justify-between border-t border-stone-300 pt-2 text-base font-bold">
                      <span>Grand Total:</span>
                      <span className="text-stone-900">{'\u20B9'}{(selectedAdditional.grand_total || selectedAdditional.total_amount || 0).toLocaleString('en-IN')}</span>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end gap-2 pt-2 border-t border-stone-200">
                  <Button variant="outline" onClick={() => setViewAdditionalDialogOpen(false)}>Close</Button>
                  {selectedAdditional.status === 'pending_review' && (
                    <>
                      <Button onClick={() => openAmendAdditionalDialog(selectedAdditional)} className="bg-orange-500 hover:bg-orange-600 text-white"><Edit3 size={14} className="mr-1" /> Request Amend</Button>
                      <Button onClick={() => handleAcceptAdditional(selectedAdditional.id)} className="bg-green-600 hover:bg-green-700 text-white"><Check size={14} className="mr-1" /> Accept</Button>
                    </>
                  )}
                  {selectedAdditional.status === 'amendment_requested' && (
                    <>
                      <Button onClick={() => openAmendAdditionalDialog(selectedAdditional)} className="bg-orange-500 hover:bg-orange-600 text-white"><Edit3 size={14} className="mr-1" /> Re-Amend</Button>
                      <Button onClick={() => handleAcceptAdditional(selectedAdditional.id)} className="bg-green-600 hover:bg-green-700 text-white"><Check size={14} className="mr-1" /> Accept as Amended</Button>
                    </>
                  )}
                  {selectedAdditional.status === 'accepted' && !selectedAdditional.converted_to_boq && (
                    <Button variant="outline" onClick={() => handleConvertAdditionalToBOQ(selectedAdditional)} className="text-purple-600 hover:bg-purple-50"><FileSpreadsheet size={14} className="mr-1" /> Convert to BOQ</Button>
                  )}
                  {selectedAdditional.converted_to_boq && <Badge className="bg-purple-100 text-purple-700 text-sm py-2 px-3"><FileSpreadsheet size={14} className="mr-1" /> Already Converted</Badge>}
                  {(user.role === 'owner' || user.role === 'admin') && (
                    <Button variant="outline" onClick={() => handleDeleteAdditional(selectedAdditional.id)} className="text-red-400 hover:text-red-600 hover:bg-red-50"><Trash2 size={14} className="mr-1" /> Delete</Button>
                  )}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* ========== VIEW SUBMISSION DIALOG ========== */}
        <Dialog open={viewSubmissionDialogOpen} onOpenChange={setViewSubmissionDialogOpen}>
          <DialogContent className="max-w-6xl w-[95vw] max-h-[90vh] overflow-y-auto" data-testid="view-submission-dialog">
            <DialogHeader>
              <DialogTitle className="text-xl text-stone-900 flex items-center gap-2">
                <Link className="text-blue-600" /> Vendor Quote Submission Details
              </DialogTitle>
            </DialogHeader>
            {selectedSubmission && (
              <div className="space-y-5">
                {/* Vendor & Project Info */}
                <div className="bg-stone-50 p-4 rounded-lg grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div><p className="text-xs text-stone-500">Vendor</p><p className="font-semibold text-stone-900">{selectedSubmission.vendor_name}</p><p className="text-sm text-stone-600">{selectedSubmission.vendor_email}</p>{selectedSubmission.vendor_phone && <p className="text-sm text-stone-500">{selectedSubmission.vendor_phone}</p>}</div>
                  <div><p className="text-xs text-stone-500">Company</p><p className="font-semibold text-stone-900 flex items-center gap-1"><Building2 size={14} className="text-stone-400" />{selectedSubmission.vendor_company || 'Not provided'}</p></div>
                  <div><p className="text-xs text-stone-500">Project</p><p className="font-semibold text-stone-900">{selectedSubmission.project_name}</p></div>
                  <div><p className="text-xs text-stone-500">Submitted</p><p className="font-semibold text-stone-900">{formatDate(new Date(selectedSubmission.submitted_at), { includeTime: true })}</p></div>
                  <div><p className="text-xs text-stone-500">Status</p>{getStatusBadge(selectedSubmission.status)}</div>
                  <div><p className="text-xs text-stone-500">Total Items</p><p className="font-semibold text-stone-900">{selectedSubmission.items?.length || 0} items</p></div>
                </div>

                {selectedSubmission.notes && <div className="bg-blue-50 p-3 rounded-lg"><p className="text-sm text-blue-800"><strong>Vendor Notes:</strong> {selectedSubmission.notes}</p></div>}

                {selectedSubmission.last_amendment_request && (
                  <div className="bg-orange-50 p-3 rounded-lg border border-orange-200">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium text-orange-800">Amendment Requested:</p>
                      {selectedSubmission.last_amendment_request.email_sent === true
                        ? <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-100 px-2 py-0.5 rounded-full" data-testid="email-sent-badge"><Mail size={10} /> Email Sent</span>
                        : selectedSubmission.last_amendment_request.email_sent === false
                          ? <span className="inline-flex items-center gap-1 text-xs font-medium text-red-700 bg-red-100 px-2 py-0.5 rounded-full" data-testid="email-failed-badge"><AlertCircle size={10} /> Email Failed</span>
                          : null
                      }
                    </div>
                    <p className="text-sm text-orange-700 mt-1">{selectedSubmission.last_amendment_request.reason}</p>
                    <p className="text-xs text-orange-500 mt-1">By {selectedSubmission.last_amendment_request.requested_by_name} on {formatDate(selectedSubmission.last_amendment_request.requested_at)}</p>
                  </div>
                )}

                {/* Quoted Items Table */}
                <div>
                  <h4 className="font-semibold text-stone-900 mb-2">Quoted Items ({selectedSubmission.items?.length || 0})</h4>
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-stone-100">
                        <tr>
                          <th className="p-2.5 text-left w-8">#</th>
                          <th className="p-2.5 text-left">Task / Item</th>
                          <th className="p-2.5 text-right w-20">Qty</th>
                          <th className="p-2.5 text-center w-20">Unit</th>
                          <th className="p-2.5 text-right w-28">Rate (₹)</th>
                          <th className="p-2.5 text-right w-32">Amount (₹)</th>
                          <th className="p-2.5 text-left">Remarks</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedSubmission.items?.map((item, idx) => {
                          const rate = item.vendor_rate || item.rate || 0;
                          const qty = item.quantity || 1;
                          const amount = rate * qty;
                          return (
                            <tr key={idx} className="border-t hover:bg-stone-50" data-testid={`submission-item-${idx}`}>
                              <td className="p-2.5 text-stone-400 text-xs">{idx + 1}</td>
                              <td className="p-2.5">
                                <p className="font-medium text-stone-800">{item.task_title || item.task_id}</p>
                                {item.description && <p className="text-xs text-stone-500 mt-0.5 line-clamp-2">{item.description}</p>}
                                {item.category_name && <p className="text-[10px] text-stone-400 mt-0.5">{item.category_name}</p>}
                                {/* iter-379 — Vendor's chosen brand */}
                                {item.selected_brand && (
                                  <p className="text-[11px] mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-800 border border-emerald-200" data-testid={`submission-item-brand-${idx}`}>
                                    <span className="uppercase text-[9px] tracking-wide text-emerald-600">Brand</span>
                                    <span className="font-semibold">{item.selected_brand}</span>
                                  </p>
                                )}
                              </td>
                              <td className="p-2.5 text-right text-stone-700">{qty}</td>
                              <td className="p-2.5 text-center text-stone-600 text-xs">{item.unit || 'Nos'}</td>
                              <td className="p-2.5 text-right font-semibold text-stone-800">₹{rate.toLocaleString('en-IN')}</td>
                              <td className="p-2.5 text-right font-bold text-green-700">₹{amount.toLocaleString('en-IN')}</td>
                              <td className="p-2.5 text-stone-500 max-w-[180px] truncate">{item.remarks || '-'}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Financial Summary */}
                <div className="bg-stone-50 rounded-lg p-4 max-w-md ml-auto">
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-stone-600">Subtotal:</span>
                      <span className="font-semibold">₹{(selectedSubmission.total_amount || 0).toLocaleString('en-IN')}</span>
                    </div>
                    {(selectedSubmission.gst_percentage > 0) && (
                      <div className="flex justify-between">
                        <span className="text-stone-600">GST ({selectedSubmission.gst_percentage}%):</span>
                        <span className="font-medium text-blue-700">+₹{(selectedSubmission.gst_amount || 0).toLocaleString('en-IN')}</span>
                      </div>
                    )}
                    {(selectedSubmission.discount_percentage > 0) && (
                      <div className="flex justify-between">
                        <span className="text-stone-600">Discount ({selectedSubmission.discount_percentage}%):</span>
                        <span className="font-medium text-red-600">-₹{(selectedSubmission.discount_amount || 0).toLocaleString('en-IN')}</span>
                      </div>
                    )}
                    <div className="flex justify-between border-t border-stone-300 pt-2 text-base font-bold">
                      <span>Grand Total:</span>
                      <span className="text-stone-900">₹{(selectedSubmission.grand_total || selectedSubmission.total_amount || 0).toLocaleString('en-IN')}</span>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end gap-2 pt-2 border-t">
                  <Button variant="outline" onClick={() => setViewSubmissionDialogOpen(false)}>Close</Button>
                  {(selectedSubmission.status === 'pending' || selectedSubmission.status === 'pending_review') && (
                    <>
                      <Button onClick={() => openAmendDialog(selectedSubmission.id)} className="bg-orange-500 hover:bg-orange-600 text-white"><Edit3 size={14} className="mr-1" /> Request Amend</Button>
                      <Button onClick={() => openActionDialog(selectedSubmission.id, 'accept')} className="bg-green-600 hover:bg-green-700 text-white"><Check size={16} className="mr-1" /> Accept</Button>
                    </>
                  )}
                  {(selectedSubmission.status === 'accepted' || selectedSubmission.status === 'approved') && !selectedSubmission.converted_to_boq && (
                    <Button onClick={() => handleConvertToBOQ(selectedSubmission)} disabled={convertToBOQLoading} className="bg-purple-600 hover:bg-purple-700 text-white"><FileSpreadsheet size={16} className="mr-1" /> {convertToBOQLoading ? 'Converting...' : 'Convert to BOQ'}</Button>
                  )}
                  {selectedSubmission.converted_to_boq && <Badge className="bg-purple-100 text-purple-700 text-sm py-2 px-3"><FileSpreadsheet size={14} className="mr-1" /> Already Converted</Badge>}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* ========== DELETE DIALOG ========== */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={(open) => { setDeleteDialogOpen(open); if (!open) setDeleteReason(''); }}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2 text-red-600"><Trash2 size={20} /> Permanently Delete Vendor Quotation</AlertDialogTitle>
              <AlertDialogDescription>
                <div className="space-y-3">
                  <p>This will permanently delete <strong>{selectedQuotation?.quotation_number}</strong> from vendor <strong>{selectedQuotation?.vendor_name}</strong>. This action cannot be undone.</p>
                  <div><label className="text-sm font-medium text-stone-700">Reason for deletion (required)</label><Textarea value={deleteReason} onChange={(e) => setDeleteReason(e.target.value)} placeholder="Enter reason..." className="mt-1" data-testid="delete-reason-input" /></div>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => setDeleteReason('')}>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} disabled={deleting || deleteReason.trim().length < 5} className="bg-red-600 hover:bg-red-700">{deleting ? 'Deleting...' : 'Delete Permanently'}</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* ========== ACCEPT/REJECT ACTION DIALOG ========== */}
        <Dialog open={actionDialogOpen} onOpenChange={setActionDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className={actionType === 'accept' ? 'text-green-700' : 'text-red-700'}>{actionType === 'accept' ? 'Accept Vendor Quote' : 'Reject Vendor Quote'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {actionType === 'accept' && <div className="bg-green-50 p-3 rounded-lg text-sm text-green-800">Accepting this quote will save the vendor rates and create tasks in Tasks & Schedule.</div>}
              {actionType === 'reject' && <div className="bg-red-50 p-3 rounded-lg text-sm text-red-800">Please provide a reason for rejecting this vendor quote.</div>}
              <div>
                <Label>Remarks {actionType === 'reject' && '*'}</Label>
                <Textarea value={actionRemarks} onChange={(e) => setActionRemarks(e.target.value)} placeholder={actionType === 'accept' ? 'Optional notes...' : 'Reason for rejection...'} rows={3} />
              </div>
              <div className="flex justify-end gap-3">
                <Button variant="outline" onClick={() => setActionDialogOpen(false)}>Cancel</Button>
                <Button onClick={handleSubmitAction} disabled={actionLoading || (actionType === 'reject' && !actionRemarks.trim())} className={actionType === 'accept' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}>
                  {actionLoading ? 'Processing...' : (actionType === 'accept' ? 'Accept & Save to BOQ' : 'Reject Quote')}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* ========== AMEND DIALOG ========== */}
        <Dialog open={amendDialogOpen} onOpenChange={setAmendDialogOpen}>
          <DialogContent className="max-w-6xl w-[95vw] max-h-[90vh] overflow-y-auto" data-testid="amend-dialog">
            <DialogHeader>
              <DialogTitle className="text-xl text-orange-700 flex items-center gap-2"><Edit3 size={20} /> Request Amendment</DialogTitle>
            </DialogHeader>
            <div className="space-y-5">
              <div className="bg-orange-50 p-3 rounded-lg text-sm text-orange-800">
                Edit individual item rates, quantities, and discounts below. The vendor will receive an email with your requested changes.
              </div>
              <div>
                <Label className="font-semibold">Reason for Amendment *</Label>
                <Textarea value={amendReason} onChange={(e) => setAmendReason(e.target.value)} placeholder="Describe what changes are needed..." rows={2} className="mt-1" data-testid="amend-reason-input" />
              </div>

              {/* CC Emails */}
              <div>
                <Label className="font-semibold text-sm">CC Emails <span className="font-normal text-stone-400">(comma-separated, optional)</span></Label>
                <Input value={amendCcEmails} onChange={(e) => setAmendCcEmails(e.target.value)} placeholder="e.g., manager@company.com, accounts@company.com" className="mt-1" data-testid="amend-cc-emails" />
              </div>

              {/* Discount Type Toggle */}
              <div className="bg-stone-50 p-4 rounded-lg border border-stone-200">
                <Label className="font-semibold text-sm mb-3 block">Discount Options</Label>
                <div className="flex gap-3">
                  <button
                    onClick={() => setAmendDiscountType('none')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                      amendDiscountType === 'none' ? 'bg-stone-900 text-white border-stone-900' : 'bg-white text-stone-700 border-stone-300 hover:bg-stone-100'
                    }`}
                    data-testid="discount-none"
                  >No Discount</button>
                  <button
                    onClick={() => setAmendDiscountType('line_item')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                      amendDiscountType === 'line_item' ? 'bg-stone-900 text-white border-stone-900' : 'bg-white text-stone-700 border-stone-300 hover:bg-stone-100'
                    }`}
                    data-testid="discount-line-item"
                  >Line-Item Discount %</button>
                  <button
                    onClick={() => setAmendDiscountType('lump_sum')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                      amendDiscountType === 'lump_sum' ? 'bg-stone-900 text-white border-stone-900' : 'bg-white text-stone-700 border-stone-300 hover:bg-stone-100'
                    }`}
                    data-testid="discount-lump-sum"
                  >Lump-Sum Discount %</button>
                </div>
                {amendDiscountType === 'lump_sum' && (
                  <div className="mt-3 flex items-center gap-3">
                    <Label className="text-sm text-stone-600 whitespace-nowrap">Overall Discount:</Label>
                    <Input
                      type="number" min="0" max="100" step="0.5"
                      value={amendLumpSumDiscount || ''}
                      onChange={(e) => setAmendLumpSumDiscount(parseFloat(e.target.value) || 0)}
                      placeholder="e.g., 10"
                      className="w-28 h-9"
                      data-testid="lump-sum-discount-input"
                    />
                    <span className="text-sm text-stone-500">% off total</span>
                  </div>
                )}
              </div>
              
              {/* Item-level editing */}
              {amendItems.length > 0 && (
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-stone-100">
                      <tr>
                        <th className="text-left px-3 py-2.5 font-medium w-8">#</th>
                        <th className="text-left px-3 py-2.5 font-medium">Task / Item</th>
                        <th className="text-center px-3 py-2.5 font-medium w-16">Unit</th>
                        <th className="text-right px-3 py-2.5 font-medium w-24">Rate ({'\u20B9'})</th>
                        <th className="text-right px-3 py-2.5 font-medium w-20">Qty</th>
                        {amendDiscountType === 'line_item' && (
                          <th className="text-right px-3 py-2.5 font-medium w-24">Disc %</th>
                        )}
                        <th className="text-right px-3 py-2.5 font-medium w-28">Amount ({'\u20B9'})</th>
                        <th className="text-left px-3 py-2.5 font-medium w-36">Remarks</th>
                      </tr>
                    </thead>
                    <tbody>
                      {amendItems.map((item, idx) => {
                        const rateChanged = item.rate !== item.original_rate;
                        const qtyChanged = item.quantity !== item.original_quantity;
                        const hasDiscount = item.discount_pct > 0;
                        const lineAmount = item.rate * item.quantity;
                        const discountedAmount = hasDiscount ? lineAmount * (1 - item.discount_pct / 100) : lineAmount;
                        return (
                          <tr key={idx} className={`border-t ${rateChanged || qtyChanged || hasDiscount ? 'bg-orange-50' : ''}`}>
                            <td className="px-3 py-2 text-stone-400 text-xs">{idx + 1}</td>
                            <td className="px-3 py-2">
                              <p className="font-medium text-stone-800 text-sm">{item.title}</p>
                              {item.description && <p className="text-xs text-stone-500 mt-0.5 line-clamp-1">{item.description}</p>}
                              {item.category_name && <p className="text-[10px] text-stone-400">{item.category_name}</p>}
                            </td>
                            <td className="px-3 py-2 text-center text-xs text-stone-600">{item.unit}</td>
                            <td className="px-3 py-2">
                              <Input
                                type="number" step="0.01" min="0"
                                value={item.rate || ''}
                                onChange={(e) => updateAmendItem(idx, 'rate', parseFloat(e.target.value) || 0)}
                                className={`h-8 text-right text-sm ${rateChanged ? 'border-orange-400 bg-orange-50' : ''}`}
                                data-testid={`amend-rate-${idx}`}
                              />
                            </td>
                            <td className="px-3 py-2">
                              <Input
                                type="number" step="0.01" min="0"
                                value={item.quantity || ''}
                                onChange={(e) => updateAmendItem(idx, 'quantity', parseFloat(e.target.value) || 0)}
                                className={`h-8 text-right text-sm ${qtyChanged ? 'border-orange-400 bg-orange-50' : ''}`}
                                data-testid={`amend-qty-${idx}`}
                              />
                            </td>
                            {amendDiscountType === 'line_item' && (
                              <td className="px-3 py-2">
                                <Input
                                  type="number" step="0.5" min="0" max="100"
                                  value={item.discount_pct || ''}
                                  onChange={(e) => updateAmendItem(idx, 'discount_pct', parseFloat(e.target.value) || 0)}
                                  className={`h-8 text-right text-sm ${hasDiscount ? 'border-red-400 bg-red-50' : ''}`}
                                  placeholder="0"
                                  data-testid={`amend-discount-${idx}`}
                                />
                              </td>
                            )}
                            <td className="px-3 py-2 text-right font-semibold text-stone-800">
                              {'\u20B9'}{Math.round(discountedAmount).toLocaleString('en-IN')}
                              {hasDiscount && <p className="text-[10px] text-red-500 line-through">{'\u20B9'}{Math.round(lineAmount).toLocaleString('en-IN')}</p>}
                            </td>
                            <td className="px-3 py-2">
                              <Input
                                value={item.remarks}
                                onChange={(e) => updateAmendItem(idx, 'remarks', e.target.value)}
                                placeholder="Note..."
                                className="h-8 text-sm"
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Amendment Summary */}
              <div className="bg-stone-50 rounded-lg p-4 max-w-md ml-auto">
                {(() => {
                  const subtotal = amendItems.reduce((s, i) => s + (i.rate * i.quantity), 0);
                  const lineItemDiscountTotal = amendDiscountType === 'line_item' 
                    ? amendItems.reduce((s, i) => s + (i.rate * i.quantity * (i.discount_pct || 0) / 100), 0) 
                    : 0;
                  const afterLineDiscount = subtotal - lineItemDiscountTotal;
                  const lumpSumDiscountAmt = amendDiscountType === 'lump_sum' ? afterLineDiscount * amendLumpSumDiscount / 100 : 0;
                  const amendedTotal = afterLineDiscount - lumpSumDiscountAmt;
                  return (
                    <div className="space-y-1.5 text-sm">
                      <div className="flex justify-between"><span className="text-stone-600">Subtotal:</span><span className="font-semibold">{'\u20B9'}{Math.round(subtotal).toLocaleString('en-IN')}</span></div>
                      {lineItemDiscountTotal > 0 && <div className="flex justify-between"><span className="text-red-600">Line-Item Discounts:</span><span className="font-medium text-red-600">-{'\u20B9'}{Math.round(lineItemDiscountTotal).toLocaleString('en-IN')}</span></div>}
                      {lumpSumDiscountAmt > 0 && <div className="flex justify-between"><span className="text-red-600">Lump-Sum Discount ({amendLumpSumDiscount}%):</span><span className="font-medium text-red-600">-{'\u20B9'}{Math.round(lumpSumDiscountAmt).toLocaleString('en-IN')}</span></div>}
                      <div className="flex justify-between border-t border-stone-300 pt-2 text-base font-bold"><span>Amended Total:</span><span className="text-stone-900">{'\u20B9'}{Math.round(amendedTotal).toLocaleString('en-IN')}</span></div>
                    </div>
                  );
                })()}
              </div>

              <div className="flex justify-end gap-3 pt-2 border-t">
                <Button variant="outline" onClick={() => setAmendDialogOpen(false)}>Cancel</Button>
                <Button onClick={handleSubmitAmend} disabled={amendLoading || !amendReason.trim()} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="submit-amend-btn">
                  {amendLoading ? 'Sending...' : 'Send Amendment Request'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* ========== AMEND ADDITIONAL ITEMS DIALOG ========== */}
        <Dialog open={amendAdditionalDialogOpen} onOpenChange={setAmendAdditionalDialogOpen}>
          <DialogContent className="max-w-6xl w-[95vw] max-h-[90vh] overflow-y-auto" data-testid="amend-additional-dialog">
            <DialogHeader>
              <DialogTitle className="text-xl text-orange-700 flex items-center gap-2"><Edit3 size={20} /> Amend Additional Items</DialogTitle>
            </DialogHeader>
            <div className="space-y-5">
              <div className="bg-orange-50 p-3 rounded-lg text-sm text-orange-800">
                Edit individual item rates, quantities, and discounts below. The vendor will receive an email with your requested changes.
              </div>
              <div>
                <Label className="font-semibold">Reason for Amendment *</Label>
                <Textarea value={amendAdditionalReason} onChange={(e) => setAmendAdditionalReason(e.target.value)} placeholder="Describe what changes are needed..." rows={2} className="mt-1" data-testid="amend-additional-reason" />
              </div>

              {/* CC Emails */}
              <div>
                <Label className="font-semibold text-sm">CC Emails <span className="font-normal text-stone-400">(comma-separated, optional)</span></Label>
                <Input value={amendAdditionalCcEmails} onChange={(e) => setAmendAdditionalCcEmails(e.target.value)} placeholder="e.g., manager@company.com, accounts@company.com" className="mt-1" data-testid="amend-additional-cc-emails" />
              </div>

              {/* Discount Type Toggle */}
              <div className="bg-stone-50 p-4 rounded-lg border border-stone-200">
                <Label className="font-semibold text-sm mb-3 block">Discount Options</Label>
                <div className="flex gap-3">
                  <button
                    onClick={() => setAmendAdditionalDiscountType('none')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                      amendAdditionalDiscountType === 'none' ? 'bg-stone-900 text-white border-stone-900' : 'bg-white text-stone-700 border-stone-300 hover:bg-stone-100'
                    }`}
                    data-testid="additional-discount-none"
                  >No Discount</button>
                  <button
                    onClick={() => setAmendAdditionalDiscountType('line_item')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                      amendAdditionalDiscountType === 'line_item' ? 'bg-stone-900 text-white border-stone-900' : 'bg-white text-stone-700 border-stone-300 hover:bg-stone-100'
                    }`}
                    data-testid="additional-discount-line-item"
                  >Line-Item Discount %</button>
                  <button
                    onClick={() => setAmendAdditionalDiscountType('lump_sum')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                      amendAdditionalDiscountType === 'lump_sum' ? 'bg-stone-900 text-white border-stone-900' : 'bg-white text-stone-700 border-stone-300 hover:bg-stone-100'
                    }`}
                    data-testid="additional-discount-lump-sum"
                  >Lump-Sum Discount %</button>
                </div>
                {amendAdditionalDiscountType === 'lump_sum' && (
                  <div className="mt-3 flex items-center gap-3">
                    <Label className="text-sm text-stone-600 whitespace-nowrap">Overall Discount:</Label>
                    <Input
                      type="number" min="0" max="100" step="0.5"
                      value={amendAdditionalLumpSumDiscount || ''}
                      onChange={(e) => setAmendAdditionalLumpSumDiscount(parseFloat(e.target.value) || 0)}
                      placeholder="e.g., 10"
                      className="w-28 h-9"
                      data-testid="additional-lump-sum-discount-input"
                    />
                    <span className="text-sm text-stone-500">% off total</span>
                  </div>
                )}
              </div>

              {/* Item-level editing */}
              {amendAdditionalItems.length > 0 && (
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-stone-100">
                      <tr>
                        <th className="text-left px-3 py-2.5 font-medium w-8">#</th>
                        <th className="text-left px-3 py-2.5 font-medium">Description</th>
                        <th className="text-center px-3 py-2.5 font-medium w-16">Unit</th>
                        <th className="text-right px-3 py-2.5 font-medium w-24">Rate ({'\u20B9'})</th>
                        <th className="text-right px-3 py-2.5 font-medium w-20">Qty</th>
                        {amendAdditionalDiscountType === 'line_item' && (
                          <th className="text-right px-3 py-2.5 font-medium w-24">Disc %</th>
                        )}
                        <th className="text-right px-3 py-2.5 font-medium w-28">Amount ({'\u20B9'})</th>
                        <th className="text-left px-3 py-2.5 font-medium w-36">Remarks</th>
                      </tr>
                    </thead>
                    <tbody>
                      {amendAdditionalItems.map((item, idx) => {
                        const rateChanged = item.rate !== item.original_rate;
                        const qtyChanged = item.quantity !== item.original_quantity;
                        const hasDiscount = item.discount_pct > 0;
                        const lineAmount = item.rate * item.quantity;
                        const discountedAmount = hasDiscount ? lineAmount * (1 - item.discount_pct / 100) : lineAmount;
                        return (
                          <tr key={idx} className={`border-t ${rateChanged || qtyChanged || hasDiscount ? 'bg-orange-50' : ''}`}>
                            <td className="px-3 py-2 text-stone-400 text-xs">{idx + 1}</td>
                            <td className="px-3 py-2">
                              <p className="font-medium text-stone-800 text-sm">{item.description}</p>
                            </td>
                            <td className="px-3 py-2 text-center text-xs text-stone-600">{item.unit}</td>
                            <td className="px-3 py-2">
                              <Input
                                type="number" step="0.01" min="0"
                                value={item.rate || ''}
                                onChange={(e) => updateAmendAdditionalItem(idx, 'rate', parseFloat(e.target.value) || 0)}
                                className={`h-8 text-right text-sm ${rateChanged ? 'border-orange-400 bg-orange-50' : ''}`}
                                data-testid={`amend-additional-rate-${idx}`}
                              />
                            </td>
                            <td className="px-3 py-2">
                              <Input
                                type="number" step="0.01" min="0"
                                value={item.quantity || ''}
                                onChange={(e) => updateAmendAdditionalItem(idx, 'quantity', parseFloat(e.target.value) || 0)}
                                className={`h-8 text-right text-sm ${qtyChanged ? 'border-orange-400 bg-orange-50' : ''}`}
                                data-testid={`amend-additional-qty-${idx}`}
                              />
                            </td>
                            {amendAdditionalDiscountType === 'line_item' && (
                              <td className="px-3 py-2">
                                <Input
                                  type="number" step="0.5" min="0" max="100"
                                  value={item.discount_pct || ''}
                                  onChange={(e) => updateAmendAdditionalItem(idx, 'discount_pct', parseFloat(e.target.value) || 0)}
                                  className={`h-8 text-right text-sm ${hasDiscount ? 'border-red-400 bg-red-50' : ''}`}
                                  placeholder="0"
                                  data-testid={`amend-additional-discount-${idx}`}
                                />
                              </td>
                            )}
                            <td className="px-3 py-2 text-right font-semibold text-stone-800">
                              {'\u20B9'}{Math.round(discountedAmount).toLocaleString('en-IN')}
                              {hasDiscount && <p className="text-[10px] text-red-500 line-through">{'\u20B9'}{Math.round(lineAmount).toLocaleString('en-IN')}</p>}
                            </td>
                            <td className="px-3 py-2">
                              <Input
                                value={item.remarks}
                                onChange={(e) => updateAmendAdditionalItem(idx, 'remarks', e.target.value)}
                                placeholder="Note..."
                                className="h-8 text-sm"
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Amendment Summary */}
              <div className="bg-stone-50 rounded-lg p-4 max-w-md ml-auto">
                {(() => {
                  const subtotal = amendAdditionalItems.reduce((s, i) => s + (i.rate * i.quantity), 0);
                  const lineItemDiscountTotal = amendAdditionalDiscountType === 'line_item'
                    ? amendAdditionalItems.reduce((s, i) => s + (i.rate * i.quantity * (i.discount_pct || 0) / 100), 0)
                    : 0;
                  const afterLineDiscount = subtotal - lineItemDiscountTotal;
                  const lumpSumDiscountAmt = amendAdditionalDiscountType === 'lump_sum' ? afterLineDiscount * amendAdditionalLumpSumDiscount / 100 : 0;
                  const amendedTotal = afterLineDiscount - lumpSumDiscountAmt;
                  return (
                    <div className="space-y-1.5 text-sm">
                      <div className="flex justify-between"><span className="text-stone-600">Subtotal:</span><span className="font-semibold">{'\u20B9'}{Math.round(subtotal).toLocaleString('en-IN')}</span></div>
                      {lineItemDiscountTotal > 0 && <div className="flex justify-between"><span className="text-red-600">Line-Item Discounts:</span><span className="font-medium text-red-600">-{'\u20B9'}{Math.round(lineItemDiscountTotal).toLocaleString('en-IN')}</span></div>}
                      {lumpSumDiscountAmt > 0 && <div className="flex justify-between"><span className="text-red-600">Lump-Sum Discount ({amendAdditionalLumpSumDiscount}%):</span><span className="font-medium text-red-600">-{'\u20B9'}{Math.round(lumpSumDiscountAmt).toLocaleString('en-IN')}</span></div>}
                      <div className="flex justify-between border-t border-stone-300 pt-2 text-base font-bold"><span>Amended Total:</span><span className="text-stone-900">{'\u20B9'}{Math.round(amendedTotal).toLocaleString('en-IN')}</span></div>
                    </div>
                  );
                })()}
              </div>

              <div className="flex justify-end gap-3 pt-2 border-t">
                <Button variant="outline" onClick={() => setAmendAdditionalDialogOpen(false)}>Cancel</Button>
                <Button onClick={handleSubmitAmendAdditional} disabled={amendAdditionalLoading || !amendAdditionalReason.trim()} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="submit-amend-additional-btn">
                  {amendAdditionalLoading ? 'Sending...' : 'Send Amendment Request'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* ========== CHAT DIALOG ========== */}
        <Dialog open={chatDialogOpen} onOpenChange={setChatDialogOpen}>
          <DialogContent className="max-w-lg max-h-[80vh] flex flex-col">
            <DialogHeader>
              <DialogTitle className="text-indigo-700 flex items-center gap-2">
                <MessageSquare size={20} /> Chat - {chatSubmission?.vendor_name}
              </DialogTitle>
            </DialogHeader>
            <div className="flex-1 overflow-y-auto space-y-3 py-3 min-h-[200px] max-h-[400px]" data-testid="chat-messages">
              {chatLoading ? (
                <div className="text-center text-stone-400 py-8">Loading messages...</div>
              ) : chatMessages.length === 0 ? (
                <div className="text-center text-stone-400 py-8">
                  <MessageSquare size={32} className="mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No messages yet. Start the conversation.</p>
                  <p className="text-xs mt-1">Messages are emailed to the vendor automatically.</p>
                </div>
              ) : (
                chatMessages.map((msg) => (
                  <div key={msg.id} className={`flex ${msg.sender_id === user.id ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[80%] rounded-lg px-3 py-2 ${msg.sender_id === user.id ? 'bg-indigo-600 text-white' : 'bg-stone-100 text-stone-800'}`}>
                      <p className={`text-xs font-medium mb-0.5 ${msg.sender_id === user.id ? 'text-indigo-200' : 'text-stone-500'}`}>{msg.sender_name}</p>
                      <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
                      <p className={`text-[10px] mt-1 ${msg.sender_id === user.id ? 'text-indigo-300' : 'text-stone-400'}`}>{new Date(msg.created_at).toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' })}</p>
                    </div>
                  </div>
                ))
              )}
            </div>
            <div className="flex gap-2 pt-3 border-t">
              <Input
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                placeholder="Type a message..."
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendChat(); } }}
                data-testid="chat-input"
              />
              <Button onClick={handleSendChat} disabled={chatSending || !chatInput.trim()} className="bg-indigo-600 hover:bg-indigo-700" data-testid="chat-send-btn">
                <Send size={16} />
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
